import { HelpCircle } from 'lucide-react';
import { forwardRef, useId, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';
import { Input } from '@/shared/components/ui/Input/Input';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/Popover/Popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/Select/Select';
import { cn } from '@/shared/utils/cn/cn.util';

import { PatternPreview } from '../PatternPreview/PatternPreview';
import { PatternTokenHelp } from '../PatternTokenHelp/PatternTokenHelp';
import { ScalarHint } from '../ScalarHint/ScalarHint';
import { ScalarShell } from '../ScalarShell/ScalarShell';
import {
  PATTERN_CUSTOM_CLASSES,
  PATTERN_FIELD_CLASSES,
  PATTERN_OPTION_CLASSES,
  PATTERN_OPTION_CODE_CLASSES,
  PATTERN_OPTION_SAMPLE_CLASSES,
  SCALAR_ROW_CLASSES,
} from '../scalar.tokens';
import { type ScalarHandle } from '../types/scalar-field.types';

import { type PatternSelectProps } from './PatternSelect.types';
import { PATTERN_CUSTOM_VALUE, usePatternSelectLogic } from './usePatternSelectLogic';

/**
 * A date format, chosen by what it renders.
 *
 * **Self-naming.** It renders a preset chooser, sometimes a custom box, and a
 * help button — three named controls, so the group carries the field's name and
 * each child a composed one.
 *
 * Every row of the list is a rendered date with its raw pattern underneath, and
 * the preview below shows the choice in both locales at once. That is the whole
 * difference from the free-text box it replaces: the operator is answering "how
 * should a date look" instead of "what is the syntax for a four-digit year".
 */
export const PatternSelect = forwardRef<ScalarHandle, PatternSelectProps>(function PatternSelect(
  {
    id,
    className,
    size,
    label,
    value,
    onValueChange,
    presets,
    isDisabled = false,
    isReadOnly = false,
    isInvalid = false,
    onBlur,
    'aria-describedby': describedBy,
  },
  ref,
) {
  const { t } = useTranslation('uiKit');
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const hintId = `${fieldId}-hint`;
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const customRef = useRef<HTMLInputElement | null>(null);

  const view = usePatternSelectLogic({
    label,
    value,
    onValueChange,
    presets,
    isDisabled,
    isReadOnly,
  });

  useImperativeHandle(ref, () => ({
    focus: () => (view.isCustom ? customRef.current : triggerRef.current)?.focus(),
    get element() {
      return view.isCustom ? customRef.current : triggerRef.current;
    },
  }));

  const describe = cn(describedBy, hintId);

  return (
    <ScalarShell
      role="group"
      className={className}
      label={label}
      aria-describedby={describe}
      isReadOnly={isReadOnly}
      announcement={view.announcement}
      hint={
        <ScalarHint id={hintId} warning={view.warning}>
          <PatternPreview rows={view.previews} />
        </ScalarHint>
      }
    >
      <span className={SCALAR_ROW_CLASSES}>
        <span className={PATTERN_FIELD_CLASSES}>
          {/*
            `disabled` is DISABLED only — never read-only. A disabled Radix
            trigger never calls `onValueChange`, so the refusal in the logic hook
            would be dead code and this would be the one control in the family
            that turns an edit away in total silence. Read-only opens, refuses,
            and says so, exactly as the duration's unit chooser does.
          */}
          <Select value={view.selected} onValueChange={view.onSelectChange} disabled={isDisabled}>
            <SelectTrigger
              ref={triggerRef}
              id={fieldId}
              fieldSize={size}
              aria-label={view.presetLabel}
              aria-describedby={describe}
              aria-readonly={isReadOnly ? true : undefined}
              onBlur={onBlur}
            >
              <SelectValue placeholder={t('pattern.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {view.presets.map((preset) => (
                <SelectItem key={preset} value={preset}>
                  <span className={PATTERN_OPTION_CLASSES}>
                    {/* The sample first: it is what the operator is choosing. */}
                    <span className={PATTERN_OPTION_SAMPLE_CLASSES}>{view.sampleOf(preset)}</span>
                    <span dir="ltr" className={PATTERN_OPTION_CODE_CLASSES}>
                      {preset}
                    </span>
                  </span>
                </SelectItem>
              ))}
              <SelectItem value={PATTERN_CUSTOM_VALUE}>{t('pattern.custom')}</SelectItem>
            </SelectContent>
          </Select>
        </span>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={view.helpLabel}
              // Reading is not writing: the reference stays available even when
              // the field itself refuses every edit.
              disabled={isDisabled}
            >
              <HelpCircle aria-hidden className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <PatternTokenHelp title={t('pattern.helpTitle')} rows={view.tokens} />
          </PopoverContent>
        </Popover>
      </span>

      {view.isCustom ? (
        <Input
          ref={customRef}
          inputSize={size}
          value={view.draft}
          // A pattern is an identifier, not prose: always LTR, monospaced, and
          // never spell-checked or autocorrected.
          dir="ltr"
          spellCheck={false}
          autoComplete="off"
          placeholder={t('pattern.customPlaceholder')}
          aria-label={view.customLabel}
          aria-describedby={describe}
          isInvalid={isInvalid || view.isDraftInvalid}
          disabled={isDisabled}
          readOnly={isReadOnly}
          onChange={(event) => {
            view.onDraftChange(event.target.value);
          }}
          onBlur={onBlur}
          className={PATTERN_CUSTOM_CLASSES}
        />
      ) : null}
    </ScalarShell>
  );
});
